import { Check, CloudUpload, Copy, Download, KeyRound, Link2, Printer, Unlink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DOLOPALS, RARITY } from "../data/dolopals";
import { normalizeExp, titanLevel } from "./TitanExpeditions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAG_KEY = "dolopals-tag";

export const getLinkedTag = () => {
  try {
    return localStorage.getItem(TAG_KEY);
  } catch {
    return null;
  }
};

export const pullVault = async (tag) => {
  try {
    const res = await fetch(`${API}/pals/save/${encodeURIComponent(tag)}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

export const pushBoardVault = async (tag, board) => {
  try {
    await fetch(`${API}/pals/save/${encodeURIComponent(tag)}/board`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ board }),
    });
  } catch {}
};

// renders a printable wallet card so kids can keep their Titan Tag offline
export const downloadTagCard = (tag) => {
  try {
    const W = 640;
    const H = 400;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#060606";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 32) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y <= H; y += 32) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
    ctx.strokeStyle = "#ff9900";
    ctx.lineWidth = 4;
    ctx.strokeRect(12, 12, W - 24, H - 24);
    ctx.setLineDash([8, 8]);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    ctx.lineWidth = 1;
    ctx.strokeRect(26, 26, W - 52, H - 52);
    ctx.setLineDash([]);
    // hazard stripe strip
    for (let i = 0; i < 24; i += 1) {
      ctx.fillStyle = i % 2 ? "#ff9900" : "#1a1a1a";
      ctx.save();
      ctx.beginPath();
      ctx.rect(40, H - 56, W - 80, 10);
      ctx.clip();
      ctx.translate(40 + i * 26, H - 56);
      ctx.rotate(-0.5);
      ctx.fillRect(0, -6, 13, 24);
      ctx.restore();
    }
    ctx.textAlign = "center";
    ctx.fillStyle = "#ff9900";
    ctx.font = "900 20px monospace";
    ctx.fillText("D O L O P A L S  //  T I T A N  T A G", W / 2, 92);
    ctx.fillStyle = "#f0ece9";
    ctx.font = "700 13px monospace";
    ctx.fillText("THIS CARD IS THE ONLY KEY TO YOUR CREW", W / 2, 122);
    ctx.fillStyle = "rgba(255, 153, 0, 0.08)";
    ctx.fillRect(70, 158, W - 140, 96);
    ctx.strokeStyle = "#ff9900";
    ctx.lineWidth = 2;
    ctx.strokeRect(70, 158, W - 140, 96);
    ctx.fillStyle = "#ff9900";
    ctx.font = "900 46px monospace";
    ctx.fillText(tag, W / 2, 220);
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.font = "700 12px monospace";
    ctx.fillText("PUNCH IT IN AT DOLOSWORLD.COM — KIDZONE", W / 2, 300);
    ctx.fillText("TITANS · SHARDS · LORE · WARLORD CROWNS", W / 2, 324);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `titan-tag-${tag.toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  } catch {}
};

const loadImg = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// printable portrait poster: ONE titan per print, with tag + game info, for bedroom walls
export const downloadTitanPoster = async (tag, pal, xp) => {
  try {
    const W = 1080;
    const H = 1400;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    const rc = RARITY[pal.rarity].color;
    ctx.fillStyle = "#060606";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 40) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y <= H; y += 40) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
    ctx.strokeStyle = "#ff9900";
    ctx.lineWidth = 6;
    ctx.strokeRect(18, 18, W - 36, H - 36);
    ctx.setLineDash([10, 10]);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.22)";
    ctx.lineWidth = 2;
    ctx.strokeRect(38, 38, W - 76, H - 76);
    ctx.setLineDash([]);

    ctx.textAlign = "center";
    ctx.fillStyle = "#ff9900";
    ctx.font = "900 34px monospace";
    ctx.fillText("D O L O P A L S  //  T I T A N  D R O P", W / 2, 112);
    ctx.fillStyle = "#f0ece9";
    ctx.font = "700 17px monospace";
    ctx.fillText("SERIES 01 // OFFICIAL ADOPTION PRINT", W / 2, 150);

    // titan art in a rarity-colored frame
    const fx = 240;
    const fy = 190;
    const fw = W - fx * 2;
    const fh = 590;
    ctx.strokeStyle = rc;
    ctx.lineWidth = 5;
    ctx.strokeRect(fx, fy, fw, fh);
    ctx.fillStyle = "#000000";
    ctx.fillRect(fx + 3, fy + 3, fw - 6, fh - 6);
    const img = await loadImg(pal.art);
    if (img) {
      const pad = 40;
      const scale = Math.min((fw - pad * 2) / img.width, (fh - pad * 2) / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.save();
      ctx.shadowColor = rc;
      ctx.shadowBlur = 42;
      ctx.drawImage(img, fx + (fw - dw) / 2, fy + (fh - dh) / 2, dw, dh);
      ctx.restore();
    }

    ctx.fillStyle = "white";
    ctx.font = "900 62px monospace";
    ctx.fillText(pal.name, W / 2, 880);
    ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
    ctx.font = "700 20px monospace";
    ctx.fillText(pal.title.toUpperCase(), W / 2, 920);
    ctx.fillStyle = rc;
    ctx.font = "900 18px monospace";
    ctx.fillText(`${RARITY[pal.rarity].label} · LVL ${titanLevel(xp)}`, W / 2, 958);

    // stat bars
    const stats = Object.entries(pal.stats);
    const sx = 250;
    const bw = 420;
    stats.forEach(([k, v], i) => {
      const y = 1008 + i * 38;
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
      ctx.font = "700 15px monospace";
      ctx.fillText(k, sx, y + 5);
      ctx.fillStyle = "rgba(255, 255, 255, 0.12)";
      ctx.fillRect(sx + 130, y - 8, bw, 14);
      ctx.fillStyle = rc;
      ctx.fillRect(sx + 130, y - 8, Math.round((bw * v) / 100), 14);
      ctx.textAlign = "right";
      ctx.fillStyle = "#f0ece9";
      ctx.fillText(String(v), sx + 130 + bw + 44, y + 5);
      ctx.textAlign = "center";
    });

    // tag + game info
    ctx.fillStyle = "#ff9900";
    ctx.font = "900 24px monospace";
    ctx.fillText(tag ? `CREW TAG // ${tag}` : "NO TITAN TAG LINKED", W / 2, 1196);
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.font = "700 14px monospace";
    ctx.fillText("UNBOX · ADOPT · SEND ON EXPEDITIONS — THE TINY TITANS IDLE GAME", W / 2, 1236);
    ctx.fillText("COMMONS 55% · RARES 32% · LEGENDARY 13% — EVERY DROP ADOPTS", W / 2, 1264);

    for (let i = 0; i < 40; i += 1) {
      ctx.fillStyle = i % 2 ? "#ff9900" : "#1a1a1a";
      ctx.save();
      ctx.beginPath();
      ctx.rect(70, H - 96, W - 140, 14);
      ctx.clip();
      ctx.translate(70 + i * 26, H - 96);
      ctx.rotate(-0.5);
      ctx.fillRect(0, -8, 13, 32);
      ctx.restore();
    }
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.font = "700 15px monospace";
    ctx.fillText("DOLOSWORLD.COM — COLLECT. BATTLE. SURVIVE. CUTELY.", W / 2, H - 46);

    await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `dolopals-${pal.id}-${(tag || "unlinked").toLowerCase()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
        resolve();
      }, "image/png");
    });
  } catch {}
};

export function usePalsSync({ collection, setCollection, exp, setExp }) {
  const [tag, setTag] = useState(() => {
    try {
      return localStorage.getItem(TAG_KEY);
    } catch {
      return null;
    }
  });
  const [status, setStatus] = useState("idle"); // idle | loading | saving | saved | error
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastPayload = useRef(null);
  const ready = useRef(false);
  const saveTimer = useRef(null);

  const pack = () => JSON.stringify({ state: { collection, exp } });

  const applyRemote = (state) => {
    const col = Array.isArray(state?.collection)
      ? state.collection.filter((id) => DOLOPALS.some((p) => p.id === id))
      : [];
    const ex = normalizeExp(state?.exp);
    setCollection(col);
    setExp(ex);
    lastPayload.current = JSON.stringify({ collection: col, exp: ex });
  };

  // Initial pull (or first push of local progress) whenever a tag becomes active.
  useEffect(() => {
    if (!tag) {
      ready.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      setStatus("loading");
      setError(null);
      try {
        const res = await fetch(`${API}/pals/save/${encodeURIComponent(tag)}`);
        if (!res.ok) {
          if (res.status === 404) {
            try {
              localStorage.removeItem(TAG_KEY);
            } catch {}
            if (!cancelled) {
              setTag(null);
              setStatus("idle");
              setError("NO SUCH TITAN TAG — CHECK THE SCRATCHES AND RETRY.");
            }
            return;
          }
          throw new Error("bad status");
        }
        const data = await res.json();
        if (cancelled) return;
        if (data.state) {
          applyRemote(data.state);
          setLastSavedAt(data.updated_at || null);
        }
        setStatus("saved");
      } catch {
        if (!cancelled) {
          setStatus("error");
          setError("CLOUD SIGNAL LOST — PROGRESS SAFE ON THIS DEVICE.");
        }
      } finally {
        if (!cancelled) ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  // Debounced auto-save after every local change (unbox, expedition, purchase, adopt).
  useEffect(() => {
    if (!tag || !ready.current) return;
    const payload = pack();
    if (payload === lastPayload.current) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      setStatus("saving");
      try {
        const res = await fetch(`${API}/pals/save/${encodeURIComponent(tag)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: payload,
        });
        if (!res.ok) throw new Error("bad status");
        const data = await res.json();
        lastPayload.current = payload;
        setLastSavedAt(data.updated_at || null);
        setStatus("saved");
      } catch {
        setStatus("error");
        setError("CLOUD SIGNAL LOST — WILL RETRY ON NEXT CHANGE.");
      }
    }, 1200);
    return () => window.clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, exp, tag]);

  const generate = async () => {
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch(`${API}/pals/tag`, { method: "POST" });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      const payload = pack();
      await fetch(`${API}/pals/save/${encodeURIComponent(data.tag)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      lastPayload.current = payload;
      try {
        localStorage.setItem(TAG_KEY, data.tag);
      } catch {}
      setTag(data.tag);
      setStatus("saved");
    } catch {
      setStatus("error");
      setError("TAG FOUNDRY UNREACHABLE — RETRY IN A MOMENT.");
    }
  };

  const link = (raw) => {
    const clean = String(raw || "")
      .toUpperCase()
      .trim()
      .replace(/[^A-Z0-9]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-|-$/g, "");
    if (clean.length < 5) {
      setError("THAT TAG LOOKS TOO SHORT — IT READS LIKE RUST-FOX-42.");
      return;
    }
    setError(null);
    try {
      localStorage.setItem(TAG_KEY, clean);
    } catch {}
    setTag(clean);
  };

  const unlink = () => {
    try {
      localStorage.removeItem(TAG_KEY);
    } catch {}
    ready.current = false;
    lastPayload.current = null;
    setTag(null);
    setStatus("idle");
    setError(null);
    setLastSavedAt(null);
  };

  return { tag, status, error, lastSavedAt, generate, link, unlink };
}

export function PalsCloudSave({ tag, status, error, lastSavedAt, generate, link, unlink, collection = [], exp = null }) {
  const [entry, setEntry] = useState("");
  const [copied, setCopied] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const ownedPals = DOLOPALS.filter((p) => collection.includes(p.id));

  const copyTag = async () => {
    try {
      await navigator.clipboard.writeText(tag);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  const statusLine = () => {
    if (status === "loading") return "TUNING THE CLOUD FREQUENCY…";
    if (status === "saving") return "SAVING TO THE CLOUD VAULT…";
    if (status === "error") return error || "CLOUD SIGNAL LOST — PROGRESS SAFE ON THIS DEVICE.";
    if (lastSavedAt) {
      const d = new Date(lastSavedAt);
      const stamp = Number.isNaN(d.getTime())
        ? ""
        : ` · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
      return `VAULT SYNCED${stamp}`;
    }
    return "VAULT LINKED — EVERY MOVE AUTO-SAVES.";
  };

  return (
    <section className="cloud-save" data-testid="cloud-save-panel">
      {!tag ? (
        <div className="cloud-save-card">
          <p className="eyebrow">CLOUD VAULT // CROSS-DEVICE SYNC</p>
          <h2>CARRY YOUR CREW ANYWHERE.</h2>
          <p className="cloud-save-copy">
            Your titans, shards, and lore live on this device only — until you claim a Titan Tag. Scratch it somewhere
            safe, punch it in on any other screen, and your whole crew follows you.
          </p>
          <div className="cloud-save-actions">
            <button
              type="button"
              className="cloud-btn cloud-btn-primary"
              onClick={generate}
              disabled={status === "loading"}
              data-testid="tag-generate-button"
            >
              <KeyRound size={14} /> {status === "loading" ? "FORGING…" : "FORGE MY TITAN TAG"}
            </button>
            <div className="cloud-link-row">
              <input
                value={entry}
                onChange={(e) => setEntry(e.target.value)}
                placeholder="HAVE A TAG? e.g. RUST-FOX-42"
                aria-label="Enter your Titan Tag"
                data-testid="tag-input"
              />
              <button
                type="button"
                className="cloud-btn"
                onClick={() => link(entry)}
                disabled={status === "loading"}
                data-testid="tag-link-button"
              >
                <Link2 size={14} /> LINK
              </button>
            </div>
          </div>
          {error ? (
            <p className="cloud-save-error" data-testid="cloud-save-error">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="cloud-save-card is-linked">
          <p className="eyebrow">CLOUD VAULT // LINKED</p>
          <div className="cloud-tag-row">
            <strong className="cloud-tag" data-testid="tag-display">
              {tag}
            </strong>
            <button type="button" className="cloud-btn" onClick={copyTag} data-testid="tag-copy-button">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "COPIED" : "COPY"}
            </button>
            <button type="button" className="cloud-btn" onClick={() => downloadTagCard(tag)} data-testid="tag-card-button">
              <Download size={14} /> SAVE CARD
            </button>
            <button type="button" className="cloud-btn" onClick={() => setPosterOpen((o) => !o)} data-testid="crew-poster-button">
              <Printer size={14} /> PRINT A TITAN
            </button>
            <button type="button" className="cloud-btn cloud-btn-ghost" onClick={unlink} data-testid="tag-unlink-button">
              <Unlink size={14} /> UNLINK
            </button>
          </div>
          {posterOpen ? (
            <div className="poster-pick" data-testid="poster-pick">
              {ownedPals.length === 0 ? (
                <p className="poster-pick-empty">NO TITANS ADOPTED YET — UNBOX A DROP FIRST.</p>
              ) : (
                ownedPals.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="poster-pick-btn"
                    onClick={() => downloadTitanPoster(tag, p, exp?.xp?.[p.id])}
                    data-testid={`poster-pick-${p.id}`}
                  >
                    <img src={p.art} alt={p.name} loading="lazy" decoding="async" />
                    <span>{p.name}</span>
                  </button>
                ))
              )}
            </div>
          ) : null}
          <p className={`cloud-save-status is-${status}`} data-testid="cloud-save-status">
            <CloudUpload size={13} /> {statusLine()}
          </p>
          <p className="cloud-save-hint">
            SCRATCH THIS TAG DOWN — IT'S THE ONLY KEY TO YOUR CREW. PROGRESS AUTO-SAVES AFTER EVERY UNBOX, EXPEDITION,
            AND PURCHASE.
          </p>
        </div>
      )}
    </section>
  );
}
